import { ChangeDetectionStrategy, Component, forwardRef, input, model } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-textarea',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiTextarea),
      multi: true,
    },
  ],
  template: `
    @if (label()) {
      <label [for]="inputId()" class="block text-sm font-medium text-gray-700 mb-1">{{ label() }}</label>
    }
    <textarea
      [id]="inputId()"
      [placeholder]="placeholder()"
      [disabled]="disabled()"
      [rows]="rows()"
      [maxLength]="maxLength()"
      [class]="textareaClasses()"
      [value]="value()"
      (input)="onInput($event)"
      (blur)="onTouched()"
      [attr.aria-label]="ariaLabel() || label() || placeholder()"
      [attr.aria-disabled]="disabled()"
    ></textarea>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class UiTextarea {
  readonly label = input<string>('');
  readonly placeholder = input<string>('');
  readonly disabled = input<boolean>(false);
  readonly rows = input<number>(3);
  readonly maxLength = input<number | null>(null);
  readonly inputId = input<string>('textarea-' + Math.random().toString(36).substring(2, 9));
  readonly ariaLabel = input<string>('');
  readonly styleClass = input<string>('');

  readonly value = model<string>('');

  onTouched: () => void = () => {};
  private onChange: (value: string) => void = () => {};

  protected textareaClasses(): string {
    const base = 'w-full px-3 py-2 text-sm border border-slate-200 rounded-lg transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-accent-500/20 focus:border-accent-500 placeholder:text-slate-400 resize-none';
    const disabled = this.disabled() ? 'bg-slate-50 text-slate-400 cursor-not-allowed' : 'bg-white hover:border-slate-300';
    return `${base} ${disabled} ${this.styleClass()}`.trim();
  }

  onInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.value.set(target.value);
    this.onChange(target.value);
  }

  writeValue(value: string): void {
    this.value.set(value || '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // handled via input
  }
}
